/**
 * IndexedDB-backed key/value store for the parent PWA's local secrets
 * (wrapped family key, client salt, endpoint list, etc.).
 *
 * The vault stores raw `Uint8Array`s under string keys — encryption is the
 * caller's job. In practice the wrapped family key is encrypted at rest with
 * a password-derived key (see `state/session.ts`) so a browser leak doesn't
 * expose the family key without the user's password.
 */

import { openDB, type IDBPDatabase } from 'idb';

const DB_NAME = 'backstop-vault';
const STORE = 'kv';
const VERSION = 1;

/**
 * Thrown when the browser's IndexedDB cannot be opened or read — a
 * corrupted backing store surfaces in Chrome as a bare "Internal error."
 * The message is user-facing: callers display `.message` as-is.
 */
export class VaultUnavailableError extends Error {
  constructor(cause: unknown) {
    super(
      'This browser could not open the Backstop data saved on this device. ' +
        'Quit your browser completely (not just this tab), open it again, and log in. ' +
        'If you still see this message, delete the stored data for this site in your browser settings, ' +
        'log in again, and choose "Recover with mnemonic". You will need your 24-word recovery phrase.',
      { cause },
    );
    this.name = 'VaultUnavailableError';
  }
}

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDb(): Promise<IDBPDatabase> {
  dbPromise ??= openDB(DB_NAME, VERSION, {
    upgrade(db) {
      db.createObjectStore(STORE);
    },
  }).catch((e: unknown) => {
    // Don't cache the rejection — a later attempt may succeed.
    dbPromise = null;
    throw e;
  });
  return dbPromise;
}

async function withDb<T>(fn: (db: IDBPDatabase) => Promise<T>): Promise<T> {
  try {
    return await fn(await getDb());
  } catch (e) {
    throw new VaultUnavailableError(e);
  }
}

/** Store `value` under `key`. Overwrites. */
export async function put(key: string, value: Uint8Array): Promise<void> {
  await withDb((db) => db.put(STORE, value, key));
}

/** Retrieve the value at `key`, or null if missing. */
export async function get(key: string): Promise<Uint8Array | null> {
  const v = (await withDb((db) => db.get(STORE, key))) as
    Uint8Array | undefined;
  return v ?? null;
}

/** Delete a single key. */
export async function del(key: string): Promise<void> {
  await withDb((db) => db.delete(STORE, key));
}

/** Wipe every key in the vault (e.g. on logout). */
export async function clear(): Promise<void> {
  await withDb((db) => db.clear(STORE));
}
